import QRCode from 'qrcode';
import {
    bodyShapeRenderer,
    eyeBallRenderer,
    eyeFrameRenderer,
} from './qrShapes';

/**
 * QR renderer: builds the module matrix with `qrcode`, then paints it with the
 * shape catalogue. Output is always SVG — raster exports rasterise this string,
 * so every format shows exactly the same artwork.
 */

const FINDER = 7;

/** @returns {{size:number, isDark:(x,y)=>boolean, inFinder:(x,y)=>boolean, finders:number[][]}} */
export function buildMatrix(payload, errorLevel = 'H') {
    const qr = QRCode.create(payload || ' ', { errorCorrectionLevel: errorLevel });
    const { size, data } = qr.modules;
    // Each finder carries the rotation that turns an asymmetric eye shape to
    // face outward, so the three corners mirror each other instead of all
    // pointing the same way.
    const finders = [
        [0, 0, 0],
        [size - FINDER, 0, 90],
        [0, size - FINDER, -90],
    ];

    const isDark = (x, y) => (x < 0 || y < 0 || x >= size || y >= size ? false : !!data[y * size + x]);
    const inFinder = (x, y) => finders.some(([fx, fy]) => x >= fx && x < fx + FINDER && y >= fy && y < fy + FINDER);

    return { size, isDark, inFinder, finders };
}

function escapeAttr(value) {
    return String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

/**
 * Solid colour or gradient reference for one part of the code.
 *
 * The gradient is laid out in user space across the whole module grid, not in
 * each shape's own box — otherwise every module repeats the full ramp instead
 * of the ramp running once across the code.
 *
 * @param {number} span width of the module grid, in the units the shapes use
 */
function paintDefs(design, id, span, gradientTransform = '') {
    if (!design.gradientEnabled) return { defs: '', paint: design.fgColor };
    const transform = gradientTransform ? ` gradientTransform="${gradientTransform}"` : '';
    const stops = `<stop offset="0" stop-color="${escapeAttr(design.gradientFrom)}"/><stop offset="1" stop-color="${escapeAttr(design.gradientTo)}"/>`;
    if (design.gradientType === 'radial') {
        return {
            defs: `<radialGradient id="${id}" gradientUnits="userSpaceOnUse"${transform} cx="${(span / 2).toFixed(3)}" cy="${(span / 2).toFixed(3)}" r="${(span * 0.7).toFixed(3)}">${stops}</radialGradient>`,
            paint: `url(#${id})`,
        };
    }
    const angle = ((design.gradientRotation || 0) * Math.PI) / 180;
    const x1 = (0.5 - Math.cos(angle) / 2) * span;
    const y1 = (0.5 - Math.sin(angle) / 2) * span;
    const x2 = (0.5 + Math.cos(angle) / 2) * span;
    const y2 = (0.5 + Math.sin(angle) / 2) * span;
    return {
        defs: `<linearGradient id="${id}" gradientUnits="userSpaceOnUse"${transform} x1="${x1.toFixed(3)}" y1="${y1.toFixed(3)}" x2="${x2.toFixed(3)}" y2="${y2.toFixed(3)}">${stops}</linearGradient>`,
        paint: `url(#${id})`,
    };
}

/**
 * Faint decorative fill behind the code. Kept at 10% alpha and drawn under the
 * modules, so it never eats into the contrast a scanner needs.
 */
function patternDefs(type, size, color) {
    if (!type || type === 'none') return { defs: '', paint: '' };
    const step = size / 26;
    const id = 'qrBgPattern';
    let body;
    if (type === 'dots') {
        body = `<circle cx="${(step / 2).toFixed(2)}" cy="${(step / 2).toFixed(2)}" r="${(step * 0.16).toFixed(2)}" fill="${escapeAttr(color)}"/>`;
    } else if (type === 'grid') {
        const w = Math.max(1, size / 600);
        body = `<path d="M0 0 H${step.toFixed(2)} M0 0 V${step.toFixed(2)}" stroke="${escapeAttr(color)}" stroke-width="${w.toFixed(2)}" fill="none"/>`;
    } else {
        const w = Math.max(1, size / 300);
        body = `<path d="M0 ${step.toFixed(2)} L${step.toFixed(2)} 0" stroke="${escapeAttr(color)}" stroke-width="${w.toFixed(2)}" fill="none"/>`;
    }
    return {
        defs: `<pattern id="${id}" width="${step.toFixed(3)}" height="${step.toFixed(3)}" patternUnits="userSpaceOnUse">${body}</pattern>`,
        paint: `url(#${id})`,
    };
}

/**
 * Render the QR code as an SVG string.
 * @param {string} payload
 * @param {object} design see DEFAULT_DESIGN in qrCodeEngine
 * @param {number} size   output width/height in px
 */
export function buildQrSvg(payload, design, size) {
    const matrix = buildMatrix(payload, design.errorLevel);
    const margin = (size * (design.quietZonePercent ?? 4)) / 100;
    const cell = (size - margin * 2) / matrix.size;

    const drawBody = bodyShapeRenderer(design.dotStyle);
    const drawFrame = eyeFrameRenderer(design.eyeFrameStyle);
    const drawBall = eyeBallRenderer(design.eyeBallStyle);

    // Logo box in module coordinates, used to clear the dots behind it.
    const hasLogo = !!design.logoDataUrl;
    const logoSpan = hasLogo ? matrix.size * ((design.logoSizePercent || 25) / 100) : 0;
    const logoPad = hasLogo ? matrix.size * ((design.logoMarginPercent ?? 1.5) / 100) : 0;
    const logoFrom = (matrix.size - logoSpan) / 2;
    const logoTo = logoFrom + logoSpan;
    const clearFrom = logoFrom - logoPad;
    const clearTo = logoTo + logoPad;
    const clearsDots = hasLogo && design.hideBackgroundDots;
    const underLogo = (x, y) => clearsDots
        && x + 1 > clearFrom && x < clearTo
        && y + 1 > clearFrom && y < clearTo;

    const bodyParts = [];
    for (let y = 0; y < matrix.size; y += 1) {
        for (let x = 0; x < matrix.size; x += 1) {
            if (!matrix.isDark(x, y) || matrix.inFinder(x, y) || underLogo(x, y)) continue;
            const near = (dx, dy) => matrix.isDark(x + dx, y + dy) && !matrix.inFinder(x + dx, y + dy);
            bodyParts.push(drawBody(x, y, {
                n: near(0, -1),
                s: near(0, 1),
                e: near(1, 0),
                w: near(-1, 0),
                ne: near(1, -1),
                nw: near(-1, -1),
                se: near(1, 1),
                sw: near(-1, 1),
            }));
        }
    }

    const bodyPaint = paintDefs(design, 'qrGradBody', matrix.size);

    // stroke-width 0 on the group: ring shapes opt in with their own width, so
    // filled shapes are not silently outlined by a module-wide stroke.
    // `rotate` orients the shape towards its own corner of the code.
    //
    // Each eye lives in its own translated (and rotated) group, so a gradient
    // measured across the whole code has to have that transform undone —
    // otherwise the eyes sample the ramp from the wrong place.
    const eyeDefs = [];
    const eyeParts = matrix.finders.map(([fx, fy, spin], index) => {
        const frameSpin = spin ? ` rotate(${spin} 3.5 3.5)` : '';
        const ballSpin = spin ? ` rotate(${spin} 1.5 1.5)` : '';
        const undo = (dx, dy, pivot) => [
            spin ? `rotate(${-spin} ${pivot} ${pivot})` : '',
            `translate(${-dx} ${-dy})`,
        ].filter(Boolean).join(' ');

        const frame = design.customEyeColors
            ? { defs: '', paint: design.eyeFrameColor }
            : paintDefs(design, `qrGradFrame${index}`, matrix.size, undo(fx, fy, 3.5));
        const ball = design.customEyeColors
            ? { defs: '', paint: design.eyeBallColor }
            : paintDefs(design, `qrGradBall${index}`, matrix.size, undo(fx + 2, fy + 2, 1.5));
        eyeDefs.push(frame.defs, ball.defs);

        return [
            `<g transform="translate(${fx} ${fy})${frameSpin}" fill="${frame.paint}" stroke="${frame.paint}" stroke-width="0">${drawFrame()}</g>`,
            `<g transform="translate(${fx + 2} ${fy + 2})${ballSpin}" fill="${ball.paint}" stroke="${ball.paint}" stroke-width="0">${drawBall()}</g>`,
        ].join('');
    }).join('');

    // Logo box in px, plus an optional rounded clip for square artwork.
    const logoX = logoFrom * cell + margin;
    const logoBox = logoSpan * cell;
    const logoRadius = (logoBox * (design.logoRadiusPercent || 0)) / 100;
    const logoClip = hasLogo && logoRadius > 0
        ? `<clipPath id="qrLogoClip"><rect x="${logoX.toFixed(2)}" y="${logoX.toFixed(2)}" width="${logoBox.toFixed(2)}" height="${logoBox.toFixed(2)}" rx="${logoRadius.toFixed(2)}"/></clipPath>`
        : '';
    const logo = hasLogo
        ? `<image href="${escapeAttr(design.logoDataUrl)}" x="${logoX.toFixed(2)}" y="${logoX.toFixed(2)}" width="${logoBox.toFixed(2)}" height="${logoBox.toFixed(2)}" preserveAspectRatio="xMidYMid meet"${logoClip ? ' clip-path="url(#qrLogoClip)"' : ''}/>`
        : '';

    // Rounded corners clip the whole artwork, so background art, pattern and
    // the code itself all share one silhouette.
    const radius = (size * (design.cornerRadiusPercent || 0)) / 100;
    const cornerClip = radius > 0
        ? `<clipPath id="qrCornerClip"><rect width="${size}" height="${size}" rx="${radius.toFixed(2)}"/></clipPath>`
        : '';
    const pattern = patternDefs(design.bgPattern, size, design.fgColor);

    const background = design.transparentBg
        ? ''
        : `<rect width="${size}" height="${size}" rx="${radius.toFixed(2)}" fill="${escapeAttr(design.bgColor)}"/>`;
    const bgImage = design.bgImageDataUrl
        ? `<image href="${escapeAttr(design.bgImageDataUrl)}" x="0" y="0" width="${size}" height="${size}" preserveAspectRatio="xMidYMid slice" opacity="${((design.bgImageOpacity ?? 20) / 100).toFixed(2)}"/>`
        : '';
    const patternRect = pattern.paint
        ? `<rect width="${size}" height="${size}" fill="${pattern.paint}" opacity="0.1"/>`
        : '';

    const defs = [bodyPaint.defs, ...eyeDefs, logoClip, cornerClip, pattern.defs]
        .filter(Boolean).join('');

    return [
        `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">`,
        defs ? `<defs>${defs}</defs>` : '',
        cornerClip ? '<g clip-path="url(#qrCornerClip)">' : '',
        background,
        bgImage,
        patternRect,
        `<g transform="translate(${margin.toFixed(3)} ${margin.toFixed(3)}) scale(${cell.toFixed(5)})" shape-rendering="geometricPrecision">`,
        `<g fill="${bodyPaint.paint}" stroke="none">${bodyParts.join('')}</g>`,
        eyeParts,
        '</g>',
        logo,
        cornerClip ? '</g>' : '',
        '</svg>',
    ].join('');
}
